import { useToast } from '@chakra-ui/react';
import {
  FinalizeOcrRequest,
  ManualOverride,
  OcrDisplayErrorCode,
  OcrItemDto,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import type { OcrPaymentProofRefetchInterval } from '@diamond/sol-buyer-context';
import {
  OCR_PAYMENT_PROOF_QUERY_KEY,
  useDeleteOcrPaymentProof,
  useFinalizeOcrPaymentProof,
  useOcrPaymentProofItems,
  useUploadOcrPaymentProof,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';

import { OcrCardState, OcrCardViewModel } from './types';

const OCR_POLLING_INTERVAL_MS = 3000;
const OCR_POLLING_TIMEOUT_MS = 30_000;

function deriveCardState(
  item: OcrItemDto,
  manualOverride: ManualOverride | undefined,
  confirmedManualIds: Set<string>
): OcrCardState {
  const code: OcrDisplayErrorCode | undefined = item.display_error?.code;
  if (code === 'DUPLICATE_PAYMENT_PROOF') return 'duplicate';
  if (
    code === 'OCR_UNREADABLE' ||
    code === 'OCR_UNAVAILABLE' ||
    code === 'OCR_TIMEOUT'
  )
    return 'manual_fallback';
  if (code !== undefined) return 'blocked';
  if (item.ocr_result !== null) {
    if (item.ocr_result.sender_bank === null) {
      return 'manual_fallback';
    }
    if (
      confirmedManualIds.has(item.id) &&
      manualOverride?.sender_account_name?.trim()
    ) {
      return 'success';
    }
    return 'needs_account_name';
  }
  return 'analyzing';
}

interface UsePaymentProofOcrFormParams {
  accessToken: string;
  orderPaymentId: string;
  onSuccess?: () => void;
}

interface ManualOverridesFormData {
  manual_overrides: ManualOverride[];
}

interface UsePaymentProofOcrFormResult {
  // Data
  cards: OcrCardViewModel[];
  submittedProofs: OcrItemDto[];

  // Loading states
  isLoadingItems: boolean;
  isSubmitting: boolean;
  isPollingTimedOut: boolean;

  // Submit availability
  canSubmit: boolean;

  // Form (for manual_overrides)
  form: UseFormReturn<ManualOverridesFormData>;

  // Actions
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (ocrJobId: string) => Promise<void>;
  confirmAccountName: (ocrJobId: string) => boolean;
  editSubmittedProofs: () => void;
  submitProof: () => Promise<void>;
  resetPollingTimer: () => void;
}

export const usePaymentProofOcrForm = ({
  accessToken,
  orderPaymentId,
  onSuccess,
}: UsePaymentProofOcrFormParams): UsePaymentProofOcrFormResult => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [submittedProofs, setSubmittedProofs] = useState<OcrItemDto[]>([]);
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const [confirmedManualIds, setConfirmedManualIds] = useState<Set<string>>(
    () => new Set()
  );
  const pollingStartRef = useRef<number | null>(null);

  const form = useForm<ManualOverridesFormData>({
    defaultValues: {
      manual_overrides: [],
    },
  });

  const manualOverrides = useWatch({
    control: form.control,
    name: 'manual_overrides',
  });

  const uploadMutation = useUploadOcrPaymentProof(accessToken);
  const deleteMutation = useDeleteOcrPaymentProof(accessToken, orderPaymentId);
  const finalizeMutation = useFinalizeOcrPaymentProof(
    accessToken,
    orderPaymentId
  );

  const getPollingInterval = useCallback<
    Exclude<NonNullable<OcrPaymentProofRefetchInterval>, number | false>
  >(
    (query) => {
      if (isPollingTimedOut) return false;

      const hasAnalyzingItem =
        query.state.data?.items.some((item) => {
          const manualOverride = manualOverrides.find(
            (override) => override.ocr_job_id === item.id
          );

          return (
            deriveCardState(item, manualOverride, confirmedManualIds) ===
            'analyzing'
          );
        }) ?? false;

      if (!hasAnalyzingItem) return false;

      if (pollingStartRef.current === null) {
        pollingStartRef.current = Date.now();
      }

      if (Date.now() - pollingStartRef.current >= OCR_POLLING_TIMEOUT_MS) {
        setIsPollingTimedOut(true);
        return false;
      }

      return OCR_POLLING_INTERVAL_MS;
    },
    [confirmedManualIds, isPollingTimedOut, manualOverrides]
  );

  const { data, isLoading: isLoadingItems } = useOcrPaymentProofItems(
    orderPaymentId,
    accessToken,
    {
      enabled: !!orderPaymentId,
      refetchInterval: getPollingInterval,
    }
  );

  // Build card view models from query data
  const cards: OcrCardViewModel[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((item) => {
      const manualOverride = manualOverrides.find(
        (override) => override.ocr_job_id === item.id
      );

      return {
        item,
        state: deriveCardState(item, manualOverride, confirmedManualIds),
      };
    });
  }, [confirmedManualIds, data, manualOverrides]);

  const hasAnalyzingCard = cards.some((c) => c.state === 'analyzing');

  useEffect(() => {
    if (!hasAnalyzingCard) {
      pollingStartRef.current = null;
    }
  }, [hasAnalyzingCard]);

  // Sync manual_overrides form entries with OCR cards that need manual input.
  useEffect(() => {
    const manualInputItems =
      data?.items.filter((item) => {
        const code = item.display_error?.code;
        const isManualFallback =
          code === 'OCR_UNREADABLE' ||
          code === 'OCR_UNAVAILABLE' ||
          code === 'OCR_TIMEOUT';
        const isBlocked =
          code !== undefined &&
          code !== 'OCR_UNREADABLE' &&
          code !== 'OCR_UNAVAILABLE' &&
          code !== 'OCR_TIMEOUT';

        return !isBlocked && (item.ocr_result !== null || isManualFallback);
      }) ?? [];
    const manualInputIds = new Set(manualInputItems.map((item) => item.id));

    setConfirmedManualIds((current) => {
      const next = new Set(
        Array.from(current).filter((id) => manualInputIds.has(id))
      );

      return next.size === current.size ? current : next;
    });

    const currentOverrides = form.getValues('manual_overrides');

    const reconciled = manualInputItems.map((item) => {
      const existing = currentOverrides.find((o) => o.ocr_job_id === item.id);
      const shouldRequireAccountName =
        item.ocr_result !== null && item.ocr_result.sender_bank !== null;

      return (
        existing ?? {
          ocr_job_id: item.id,
          sender_bank: item.ocr_result?.sender_bank ?? '',
          sender_account_name: shouldRequireAccountName
            ? ''
            : item.ocr_result?.sender_account_name ?? '',
        }
      );
    });

    const isSame =
      currentOverrides.length === reconciled.length &&
      reconciled.every(
        (r, i) =>
          currentOverrides[i]?.ocr_job_id === r.ocr_job_id &&
          currentOverrides[i]?.sender_bank === r.sender_bank &&
          currentOverrides[i]?.sender_account_name === r.sender_account_name
      );

    if (!isSame) {
      form.setValue('manual_overrides', reconciled);
    }
  }, [data?.items, form]);

  const confirmAccountName = useCallback(
    (ocrJobId: string) => {
      const index = form
        .getValues('manual_overrides')
        .findIndex((override) => override.ocr_job_id === ocrJobId);

      if (index < 0) return false;

      const fieldName =
        `manual_overrides.${index}.sender_account_name` as const;
      const value = form.getValues(fieldName)?.trim() ?? '';

      if (!value) {
        form.setError(fieldName, {
          message: 'Nama pemilik rekening harus diisi',
          type: 'required',
        });
        return false;
      }

      form.clearErrors(fieldName);
      form.setValue(fieldName, value);
      setConfirmedManualIds((current) => new Set(current).add(ocrJobId));
      return true;
    },
    [form]
  );

  const isSubmitting =
    finalizeMutation.isPending ||
    uploadMutation.isPending ||
    deleteMutation.isPending;

  const canSubmit = useMemo(() => {
    if (isSubmitting) return false;
    if (cards.length === 0) return false;
    if (
      cards.some(
        (c) =>
          c.state === 'analyzing' ||
          c.state === 'needs_account_name' ||
          c.state === 'duplicate' ||
          c.state === 'blocked'
      )
    )
      return false;

    const hasIncompleteManual = cards.some((c) => {
      if (c.state !== 'manual_fallback') return false;
      const override = manualOverrides.find((o) => o.ocr_job_id === c.item.id);
      return !override?.sender_bank || !override?.sender_account_name;
    });

    return !hasIncompleteManual;
  }, [isSubmitting, cards, manualOverrides]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      try {
        await uploadMutation.mutateAsync({ orderPaymentId, files });
        // Reset polling timer so a new 30s budget starts for freshly uploaded files
        pollingStartRef.current = null;
        setIsPollingTimedOut(false);
        await queryClient.invalidateQueries({
          queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Terjadi kesalahan';
        showToast(toast, 'error', message);
      }
    },
    [uploadMutation, orderPaymentId, queryClient, toast]
  );

  const deleteFile = useCallback(
    async (ocrJobId: string) => {
      try {
        await deleteMutation.mutateAsync(ocrJobId);
        setSubmittedProofs([]);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Terjadi kesalahan';
        showToast(toast, 'error', message);
      }
    },
    [deleteMutation, toast]
  );

  const submitProof = useCallback(async () => {
    const validCards = cards.filter(
      (c) => c.state === 'success' || c.state === 'manual_fallback'
    );
    const ocr_job_ids = validCards.map((c) => c.item.id);
    const manual_overrides = form
      .getValues('manual_overrides')
      .filter((o) => ocr_job_ids.includes(o.ocr_job_id));

    const body: FinalizeOcrRequest = { ocr_job_ids, manual_overrides };

    try {
      await finalizeMutation.mutateAsync({
        paymentId: orderPaymentId,
        body,
      });
      setSubmittedProofs(validCards.map((card) => card.item));
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Terjadi kesalahan';
      showToast(toast, 'error', message);
    }
  }, [cards, finalizeMutation, form, onSuccess, orderPaymentId, toast]);

  const editSubmittedProofs = useCallback(() => {
    setSubmittedProofs([]);
  }, []);

  const resetPollingTimer = useCallback(() => {
    pollingStartRef.current = null;
    setIsPollingTimedOut(false);
  }, []);

  return {
    cards,
    submittedProofs,
    isLoadingItems,
    isSubmitting,
    isPollingTimedOut,
    canSubmit,
    form,
    uploadFiles,
    deleteFile,
    confirmAccountName,
    editSubmittedProofs,
    submitProof,
    resetPollingTimer,
  };
};
